// SCHEDULER surface (N-BOARD-3), the "Signal" re-layout (Scheduler Screen v2): a CALENDAR view (a month of
// SERVER-projected occurrences and RECORDED runs, steppable Previous/Next, plus the selected day's agenda
// and the honest at-a-glance card), a LIST view (lifecycle tabs + search over the Signal routine rows), a
// ROUTINE DETAIL panel opened from either, and the registered Background Jobs on the collapsible "Ritona's
// own upkeep" shelf — all run through the ONE scheduler mechanism (G-112). A `failed` or `interrupted` run
// stays VISIBLY DISTINCT from a clean `completed` one everywhere it appears (AC-2, G-107/G-111) — the
// surface renders the distinction the `scheduler` module records, never its own heuristic (G-182). The
// calendar places occurrences the scheduler module projected (deterministic + bounded, G-110); the client
// computes no recurrence. "New routine" is a chat handoff, never a client-side write.
//
// DECISION-ROUTING ONLY. Every action here — Run now, Pause, Resume, Remove — routes to the owning module
// and re-reads the board; the dashboard sets no state field itself (P-GATE). Nothing pends: pausing a
// routine is a click, not an approval queue. A refusal comes back as the owning module's own sanitised
// note and is rendered verbatim.
//
// ALL FOUR ACTIONS ARE REACHED THROUGH THE DETAIL PANEL. The list row and the day agenda open a routine and
// nothing more, so the board reads as a board (the owner's 11 Aug 2026 complaint); the routes therefore
// reach RoutineDetail alone. The panel is looked up in the LIVE projection each render, so lining routines
// up and clicking through them swaps the panel in place, and a routine that leaves the board closes it.
//
// Thin over services — a pure read of the `scheduler` health view. Tokens only (G-181).

import { useCallback, useState } from 'react';
import { useSurfaceApi } from './SurfaceApiContext.tsx';
import { useProjection } from './useProjection.ts';
import { SurfaceFrame } from './surface-frame.tsx';
import { RoutineCalendar } from './components/RoutineCalendar.tsx';
import { RoutineList, type RoutineTab } from './components/RoutineList.tsx';
import { RoutineDetail } from './components/RoutineDetail.tsx';
import type { RoutineRoute, RoutineRoutes } from './components/RoutineActions.tsx';
import { UpkeepShelf } from './components/UpkeepShelf.tsx';
import { localIsoDate, shiftMonth, stateCounts } from './components/scheduler-format.ts';
import { ScreenToolbar, ScreenBody, TbStamp, TbInfo } from '../app/screen-chrome.tsx';
import { Icon } from '../app/Icon.tsx';
import { useShell } from '../app/ShellContext.tsx';
import type { ScheduleHealthProjection, ScheduleHealthScheduleRow } from './projections.ts';
import type { RouteResult, SurfaceApi } from './api-client.ts';

/** Wrap one routed leg: re-read the board on ok so the row changes because the BOARD says so, never
 *  because the client changed it (G-182); on a refusal surface the owning module's note verbatim. */
function useRoute(
  leg: (scheduleId: string) => Promise<RouteResult>,
  reload: () => void,
  setNote: (note: string | null) => void,
): RoutineRoute {
  return useCallback(
    async (scheduleId: string): Promise<RouteResult> => {
      const result = await leg(scheduleId);
      if (result.ok) {
        setNote(null);
        reload();
      } else {
        setNote(result.note ?? 'That action was not permitted.');
      }
      return result;
    },
    [leg, reload, setNote],
  );
}

/** The five routed per-routine intents. Each names the owning module's own verb — the dashboard adds no
 *  authority check of its own, because the OWNING module is the boundary (G-180) and only it can answer. */
function useRoutineRoutes(
  api: SurfaceApi,
  reload: () => void,
  setNote: (note: string | null) => void,
): RoutineRoutes {
  return {
    onRunNow: useRoute(api.runScheduleNow, reload, setNote),
    // PAUSE / RESUME reach `scheduler.pauseSchedule` / `resumeSchedule` — module methods that existed
    // from Stage F with no route and no control, so `paused` was a state nothing could enter or leave.
    onPause: useRoute(api.pauseSchedule, reload, setNote),
    onResume: useRoute(api.resumeSchedule, reload, setNote),
    onRemove: useRoute(api.removeSchedule, reload, setNote),
    // RESTORE reaches `scheduler.restoreSchedule` — archived → paused, the human's way back after Ritona
    // archived a routine on someone's request (owner ruling 2 Sep 2026).
    onRestore: useRoute(api.restoreSchedule, reload, setNote),
  };
}

/** The board's toolbar stamp — the lifecycle roll-up, shown only once a projection has actually loaded. */
function SchedulerToolbar({
  schedules,
}: {
  readonly schedules: readonly ScheduleHealthScheduleRow[] | null;
}): React.JSX.Element {
  const counts = stateCounts(schedules ?? []);
  return (
    <ScreenToolbar title="Scheduler">
      {schedules !== null ? (
        <TbStamp>
          {counts.live} live · {counts.paused} paused · {counts.draft}{' '}
          {counts.draft === 1 ? 'draft' : 'drafts'}
        </TbStamp>
      ) : null}
      <TbInfo
        title="Scheduler"
        tip="The routines Ritona runs on a clock — collecting values, watching for drift, reflecting overnight. Every run is logged; nothing acts without its gate."
      />
    </ScreenToolbar>
  );
}

/** The Scheduler surface. */
export function SchedulerSurface(): React.JSX.Element {
  const api = useSurfaceApi();
  const shell = useShell();
  const state = useProjection<ScheduleHealthProjection>(api.getScheduleHealth);
  const [view, setView] = useState<'calendar' | 'list'>('calendar');
  const [tab, setTab] = useState<RoutineTab>('all');
  const [selectedDay, setSelectedDay] = useState(() => localIsoDate(new Date()));
  const [monthAnchor, setMonthAnchor] = useState(() => new Date());
  const [openScheduleId, setOpenScheduleId] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const newRoutine = (): void =>
    shell.openNewChatWithDraft('Set up a new routine. What it should do, and when it should run: ');
  const routes = useRoutineRoutes(api, state.reload, setNote);

  return (
    <>
      <SchedulerToolbar schedules={state.data?.schedules ?? null} />
      <ScreenBody>
        <SurfaceFrame state={state}>
          {(health) => (
            <SchedulerBody
              health={health}
              view={view}
              onView={setView}
              tab={tab}
              onTab={setTab}
              selectedDay={selectedDay}
              onSelectDay={setSelectedDay}
              monthAnchor={monthAnchor}
              onStepMonth={(step) => setMonthAnchor((anchor) => shiftMonth(anchor, step))}
              openScheduleId={openScheduleId}
              onOpenRoutine={setOpenScheduleId}
              note={note}
              onNewRoutine={newRoutine}
              routes={routes}
            />
          )}
        </SurfaceFrame>
      </ScreenBody>
    </>
  );
}

/** The Calendar | List segment + the New-routine chat handoff (the body header row). */
function ViewControls({
  view,
  onView,
  onNewRoutine,
}: {
  readonly view: 'calendar' | 'list';
  readonly onView: (view: 'calendar' | 'list') => void;
  readonly onNewRoutine: () => void;
}): React.JSX.Element {
  return (
    <div className="rt-head">
      <div role="group" aria-label="View" className="rt-seg">
        <button
          type="button"
          className="sg"
          aria-pressed={view === 'calendar'}
          onClick={() => onView('calendar')}
        >
          <Icon name="today" size={14} /> Calendar
        </button>
        <button
          type="button"
          className="sg"
          aria-pressed={view === 'list'}
          onClick={() => onView('list')}
        >
          <Icon name="tasks" size={14} /> List
        </button>
      </div>
      <span className="spacer" aria-hidden="true" />
      {/* "New routine" hands a draft to the global chat: only Ritona can PROPOSE a Schedule, so the
          screen never writes one (P-GATE). This is the design's Ask-Ritona affordance for this board. */}
      <button type="button" className="tb-new" onClick={onNewRoutine} aria-label="New routine">
        <Icon name="plus" size={13} /> New routine
      </button>
    </div>
  );
}

/** What the loaded body renders and routes (named so the component stays under the size gate). */
interface SchedulerBodyProps {
  readonly health: ScheduleHealthProjection;
  readonly view: 'calendar' | 'list';
  readonly onView: (view: 'calendar' | 'list') => void;
  readonly tab: RoutineTab;
  readonly onTab: (tab: RoutineTab) => void;
  readonly selectedDay: string;
  readonly onSelectDay: (day: string) => void;
  readonly monthAnchor: Date;
  readonly onStepMonth: (step: number) => void;
  readonly openScheduleId: string | null;
  readonly onOpenRoutine: (scheduleId: string | null) => void;
  readonly note: string | null;
  readonly onNewRoutine: () => void;
  readonly routes: RoutineRoutes;
}

/** The chosen view — calendar or list — with the shared open/step/route wiring. */
function SchedulerStage(props: SchedulerBodyProps): React.JSX.Element {
  const showList = (nextTab: RoutineTab): void => {
    props.onTab(nextTab);
    props.onView('list');
  };
  if (props.view === 'calendar') {
    return (
      <RoutineCalendar
        schedules={props.health.schedules}
        monthAnchor={props.monthAnchor}
        onStepMonth={props.onStepMonth}
        selectedDay={props.selectedDay}
        onSelectDay={props.onSelectDay}
        onOpenRoutine={props.onOpenRoutine}
        onShowPaused={() => showList('paused')}
        onShowDrafts={() => showList('draft')}
      />
    );
  }
  return (
    <RoutineList
      schedules={props.health.schedules}
      tab={props.tab}
      onTab={props.onTab}
      onOpen={props.onOpenRoutine}
      openScheduleId={props.openScheduleId}
    />
  );
}

/** The routine the detail panel is showing — looked up in the LIVE projection each render, so a routine
 *  that has been removed (or has left the board any other way) closes the panel instead of freezing a
 *  stale copy of a row that no longer exists. */
function openRow(
  schedules: readonly ScheduleHealthScheduleRow[],
  openScheduleId: string | null,
): ScheduleHealthScheduleRow | undefined {
  if (openScheduleId === null) return undefined;
  return schedules.find((row) => row.scheduleId === openScheduleId);
}

/** The loaded Scheduler body — the view controls, the chosen view + detail panel, and the upkeep shelf. */
function SchedulerBody(props: SchedulerBodyProps): React.JSX.Element {
  const detail = openRow(props.health.schedules, props.openScheduleId);
  return (
    <>
      <ViewControls view={props.view} onView={props.onView} onNewRoutine={props.onNewRoutine} />
      <section aria-label="Schedules" className="rt-body">
        <div className="rt-stage">
          <div className="rt-stage-main">
            <SchedulerStage {...props} />
          </div>
          {/* THE ROUTINE IS THE PANEL'S IDENTITY, hence the key. Without it React keeps one panel instance
              alive across a swap and re-renders it in place, so every scrap of panel-local state — an armed
              removal, an in-flight guard — silently transfers to whatever routine is opened next. Clicking
              along the list is the way the owner reads the board, so that swap is the common path, not an
              edge case. Keyed on the id (not the row object) so a background re-read of the SAME routine
              still leaves the panel, and anything the human has started in it, exactly as it was. */}
          {detail !== undefined ? (
            <RoutineDetail
              key={detail.scheduleId}
              row={detail}
              routes={props.routes}
              onClose={() => props.onOpenRoutine(null)}
            />
          ) : null}
        </div>
        {/* ONE refusal note for the whole board, whichever control was clicked: the owning module's own
            sanitised reason, rendered verbatim. The dashboard never authors a reason of its own. */}
        {props.note !== null ? (
          <p role="status" className="gate-note">
            {props.note}
          </p>
        ) : null}
      </section>
      <UpkeepShelf jobs={props.health.backgroundJobs} />
    </>
  );
}
